import {Types}                  from "@Common/types";
import {parseDate, toJsDate}    from "@Common/utils/date-object";
import {filterKeys} from "@Common/utils/objects";

type ApiListingModel     = Types.Apis.Payloads.Listings.Listings.CreateListing.Listing;
type FormListingModel    = Types.Xpresso.Account.Admin.Listings.ListingModel;
type GetListingResponse  = Types.Apis.Payloads.Listings.GetListing.Response;
type ListingPictureIndex = 0|1|2|3|4|5|6|7|8|9;

const LISTING_MODEL_PAYLOAD_MATCHING_KEYS = [
    "mlsId",
    "mlsName",
    "bathrooms",
    "bedrooms",
    "description",
    "firePlace",
    "listedPrice",
    "listingUrl",
    "lotSize",
    "propertyType",
    "roomCount",
    "soldPrice",
    "title",
    "virtualTourUrl",
    "yearBuilt",
] as const;

export const mapApiModelForForm = (listing:GetListingResponse):FormListingModel => ({
    ...filterKeys(
        listing,
        (key) => LISTING_MODEL_PAYLOAD_MATCHING_KEYS.includes(key as any)
    ) as Pick<Required<GetListingResponse>, typeof LISTING_MODEL_PAYLOAD_MATCHING_KEYS[number]>,
    agent                 : (
        listing.idAgent
        ? {
            idAgent: listing.idAgent,
            agentEmail: listing.agent.emailAddress,
            agentNameFirst: listing.agent.firstName,
            agentNameLast: listing.agent.lastName,
        }
        : null
    ),
    branch                : listing.idBranch ? {idBranch: listing.idBranch} : null,
    agentEmailAddress     : listing.agent.emailAddress,
    dateListed            : listing.dateListed ? parseDate(listing.dateListed) : null,
    dateSold              : listing.dateSold   ? parseDate(listing.dateSold) : null,
    eventNotifyAgent      : false,
    listingType           : listing.dateSold ? "js" : "jl",
    locationCity          : listing.location.city,
    locationState         : listing.location.state,
    locationStreetAddress : listing.location.streetAddress,
    locationUnitNumber    : listing.location.unitNumber,
    locationZip           : listing.location.zip,
    lotSizeSquareFeet     : listing.lotSizeSquareFeet === "" ? null : listing.lotSizeSquareFeet,
    primaryBuyerFirstName : listing.buyerSeller?.[0]?.primaryBuyerFirstName,
    primaryBuyerLastName  : listing.buyerSeller?.[0]?.primaryBuyerLastName,
    soldPrice             : listing.soldPrice,
    ...Array.isArray(listing.openHouse) && listing.openHouse.length > 0
        ? {
            openHouseDate      : parseDate(listing.openHouse[0].date),
            openHouseStartTime : mapApiTimeStringAsTimestamp(listing.openHouse[0].startTime),
            openHouseEndTime   : mapApiTimeStringAsTimestamp(listing.openHouse[0].endTime),
        }
        : {
            openHouseDate      : null,
            openHouseStartTime : null,
            openHouseEndTime   : null,
        }
    ,
    ...listing.pictures?.slice(0,10).reduce(
        (agg, picture, index:ListingPictureIndex) => ({...agg, [`picture${index}`]: picture.pictureUrl}),
        {} as {[key in `picture${ListingPictureIndex}`]: string}
    ),
});

const mapApiTimeStringAsTimestamp = (timeString:string): number => (
    new Date(`1970-01-01T${timeString?.slice(0,5)}Z`).valueOf()
);

export const mapFormModelForApi = (listing:FormListingModel): ApiListingModel => ({
    ...listing.dateListed && {dateListed: toJsDate(listing.dateListed, 0).toISOString()},
    ...listing.dateSold   && {dateSold: toJsDate(listing.dateSold, 0).toISOString()},
    ...listing.listingType === "js" && {soldPrice: Number(listing.soldPrice) || 0},
    bathrooms         : Number(listing.bathrooms) || 0,
    bedrooms          : Number(listing.bedrooms) || 0,
    description       : listing.description || "",
    firePlace         : !!listing.firePlace,
    idAgent           : listing.agent.idAgent,
    idBranch          : listing.branch.idBranch,
    listedPrice       : Number(listing.listedPrice) || 0,
    listingUrl        : listing.listingUrl || "",
    lotSize           : listing.lotSize || "",
    lotSizeSquareFeet : Number(listing.lotSizeSquareFeet) || 0,
    mlsId             : String(listing.mlsId),
    mlsName           : listing.mlsName || "",
    propertyType      : listing.propertyType || "",
    roomCount         : Number(listing.roomCount || 0),
    title             : listing.title || "",
    virtualTourUrl    : listing.virtualTourUrl || "",
    yearBuilt         : String(listing.yearBuilt || ""),
    agent             : {
        firstName    : listing.agent.agentNameFirst || "",
        lastName     : listing.agent.agentNameLast || "",
        emailAddress : listing.agentEmailAddress || "",
    },
    location          : {
        city          : listing.locationCity || "",
        state         : listing.locationState || "",
        streetAddress : listing.locationStreetAddress || "",
        unitNumber    : String(listing.locationUnitNumber || ""),
        zip           : String(listing.locationZip || ""),
    },
    openHouse         : listing.openHouseDate && listing.openHouseStartTime && listing.openHouseEndTime
        ? [{
            date      : toJsDate(listing.openHouseDate).toISOString().slice(0, 10),
            startTime : mapTimestampAsApiTimeString(listing.openHouseStartTime),
            endTime   : mapTimestampAsApiTimeString(listing.openHouseEndTime),
        }]
        : []
    ,
    pictures   : (
        new Array(10)
            .fill(null)
            .map((...[,index]:[any, ListingPictureIndex]) => ({pictureUrl: listing[`picture${index}`], thumbUrl: listing[`picture${index}`]}))
            .filter((picItem) => picItem.pictureUrl) // keep only filled pictures
    ),
});

const mapTimestampAsApiTimeString = (timestamp:number):string => (
    new Date(timestamp).toLocaleTimeString("en-US", {timeZone: "UTC", hourCycle: "h23"}).slice(0,5)
);


